import { Component, Inject, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LibraryDocument, FileType } from '../../library/library.model';
import { DatabaseService } from '../database.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {switchMap, tap} from 'rxjs/operators';

export interface DocumentViewerData {
  document: LibraryDocument;
}

@Component({
  selector: 'app-document-viewer',
  templateUrl: './document-viewer.component.html',
  styleUrls: ['./document-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DocumentViewerComponent implements OnInit {
  private databaseService = inject(DatabaseService);
  private sanitizer = inject(DomSanitizer);
  private dialogRef = inject(MatDialogRef<DocumentViewerComponent>);
  private data = inject(MAT_DIALOG_DATA) as LibraryDocument;

  fileUrl: SafeResourceUrl | null = null;
  fileType: 'pdf' | 'image' | 'unsupported' = 'unsupported';
  loading = true;
  error = false;
  errorMessage = '';

  document = this.data;

  ngOnInit(): void {
    this.determineFileType();
    this.loadDocumentContent();
  }

  private determineFileType(): void {
    if (!this.document.path) {
      this.fileType = 'unsupported';
      return;
    }

    const extension = this.document.path.toLowerCase().split('.').pop();

    switch (extension) {
      case 'pdf':
        this.fileType = 'pdf';
        break;
      case 'jpg':
      case 'jpeg':
      case 'png':
        this.fileType = 'image';
        break;
      default:
        this.fileType = 'unsupported';
    }
  }

  loadDocumentContent(): void {
    if (this.fileType === 'unsupported') {
      this.loading = false;
      this.error = true;
      this.errorMessage = 'Unsupported file type';
      return;
    }

    // Assuming your DatabaseService has a method to get document content
    this.databaseService.getFileById(this.document.path)
      .pipe(
        switchMap((res) => this.databaseService.getFileFromStorage(res))
      )
      .subscribe({
      next: (response) => {
        this.createFileUrl(response);
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading document content:', err);
        this.error = true;
        this.errorMessage = 'Failed to load document content';
        this.loading = false;
      }
    });
  }

  private createFileUrl(content: Blob): void {
    try {
      // Correct MIME type for PDF
      let mimeType = content.type;

      if (this.fileType === 'pdf' && mimeType !== 'application/pdf') {
        mimeType = 'application/pdf';
      } else if (this.fileType === 'image' && !mimeType.startsWith('image/')) {
        // You might know the image type, set accordingly
        mimeType = 'image/png';
      }

      // Recreate the blob with correct MIME type if necessary
      const correctedBlob = new Blob([content], { type: mimeType });

      const url = URL.createObjectURL(correctedBlob);

      if (this.fileType === 'pdf') {
        this.fileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      } else if (this.fileType === 'image') {
        this.fileUrl = this.sanitizer.bypassSecurityTrustUrl(url);
      }
    } catch (error) {
      console.error('Error creating file URL:', error);
      this.error = true;
      this.errorMessage = 'Failed to create file URL';
    }
  }

  close(): void {
    // Clean up object URL to prevent memory leaks
    if (this.fileUrl) {
      const url = (this.fileUrl as any).changingThisBreaksApplicationSecurity;
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    }
    this.dialogRef.close();
  }

  private correctFileName(fileName: string, url: string): string {
    let tmpName = fileName;
    if (!fileName){
     tmpName = 'tmp';
    }
    const extensionFile = fileName.toLowerCase().split('.').pop();
    const extensionUrl =  url.toLowerCase().split('.').pop();
    if( ['pdf', 'jpg', 'jpeg', 'png'].includes(extensionFile) && extensionFile === extensionFile ){
      // extensions match
      return tmpName;
    }
    return tmpName + '.'+ extensionUrl;

  }

  download(): void {
    if (this.fileUrl) {
      const url = (this.fileUrl as any).changingThisBreaksApplicationSecurity;
      const link = document.createElement('a');
      link.href = url;
      link.download = this.correctFileName(this.document.name,this.document.path);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  getFileIcon(): string {
    switch (this.fileType) {
      case 'pdf':
        return 'picture_as_pdf';
      case 'image':
        return 'image';
      default:
        return 'description';
    }
  }

  getFileSizeFormatted(): string {
    if (!this.document.fileSize) return 'Unknown size';

    const bytes = this.document.fileSize;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];

    if (bytes === 0) return '0 Bytes';

    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  }
}
